import './Verify.scss'

import { useState, useEffect } from 'react'
import {
  Col,
  Container,
  Row,
  Form,
  Button,
  Spinner,
  FloatingLabel
} from 'react-bootstrap'

import RembleLogomark from '../../assets/images/Remble_Logomark-Green.png'

import {
  authenticateUser,
  verifyUser,
  sendNewVerificationCode
} from '../common/api/Users'
import {
  saveUserEvent,
  EVENT_CATEGORY_ACCOUNT,
  EVENT_ACTION_LOGIN
} from '../common/api/UserEvents'
import { fetchVerificationData, login } from '../auth'

import { navigateTo } from '../common/navigation'
import { ERR_USER_ALREADY_CONFIRMED } from '../common/api'
import { ROUTE_SIGNUP } from '../common/helpers/Paths'

export default function Verify({
  history,
  verifiedURL,
  setIsAuthenticated,
  setHeaderTitle,
  setReturnPath
}) {
  // Try to fetch the verification data
  const { email, password } = fetchVerificationData() || {
    email: '',
    password: ''
  }

  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [verificationError, setVerificationError] = useState()
  const [codeSent, setCodeSent] = useState(false)

  const requestNewCode = async () => {
    // Reset the form
    setLoading(true)
    setVerificationError(null)
    setCode('')
    setCodeSent(true)

    // Resend the verification email
    const sent = await sendNewVerificationCode(email)

    // If the email was not sent, show an error
    if (!sent) {
      setVerificationError(
        'Unable to send new verification code. Please contact Remble support: support@remble.com.'
      )
    }

    setLoading(false)
  }

  const attemptLogin = async () => {
    await authenticateUser({
      email,
      password
    }).then(async result => {
      // Success
      if (result.token) return await handleAuthSuccess(result, verifiedURL)

      // Did we have an exception?
      if (result.detail) setVerificationError(result.detail)
    })
  }

  const handleAuthSuccess = async (data, redirectURL) => {
    login(data)
    await saveUserEvent(EVENT_CATEGORY_ACCOUNT, EVENT_ACTION_LOGIN)
    setIsAuthenticated(true)

    // IMPORTANT NOTE!
    // Don't post to mobile yet, so we can make sure the user completes onboarding
    // Post to mobile keychain after onboarding in Welcome.jsx
    return navigateTo(redirectURL, history)
  }

  const handleSubmit = async event => {
    // Prevent event bubbling
    event.preventDefault()
    event.stopPropagation()

    // Don't proceed if we're missing data
    if (!email || !code) return

    setLoading(true)
    setCodeSent(false)

    await verifyUser({
      email,
      code
    })
      .then(async result => {
        if (result.verified) return await attemptLogin()

        // All loads complete
        setLoading(false)

        // Is the code invalid or expired?
        if (result.detail) {
          // Did the user already confirm, but got stuck in the verification loop?
          if (result.detail === ERR_USER_ALREADY_CONFIRMED)
            return await attemptLogin()

          setVerificationError(result.detail)
        }
      })
      .catch(error => console.error(error))
  }

  useEffect(() => {
    // Clear out Header title and return path
    setHeaderTitle(null)
    setReturnPath(ROUTE_SIGNUP)

    // Are we missing the email or password?
    if (!email || !password)
      setVerificationError(
        'Failed to decrypt verification data. Please contact Remble support.'
      )
  }, [setHeaderTitle, setReturnPath, email, password])

  return (
    <Container fluid noValidate className='verify-container'>
      <Row>
        <Col>
          <img
            className='logomark'
            src={RembleLogomark}
            alt='Remble'
            width='25'
            fetchpriority='high'
          />
          <h1 className='mt-3 title'>Check Your Email</h1>
          <p className='subtitle'>
            We’ve sent a Remble account verification code to your email address,{' '}
            <span className='muted'>{email}</span>, to verify your account.
          </p>
        </Col>
      </Row>
      <Row>
        <Form className='verify-form' onSubmit={handleSubmit}>
          <Form.Group className='my-3' controlId='formCode'>
            <FloatingLabel
              controlId='floatingCode'
              label='Account verification code'
            >
              <Form.Control
                name='code'
                disabled={!!loading}
                size='lg'
                placeholder='Account verification code'
                required
                autoFocus={true}
                autoCapitalize='off'
                type='text'
                value={code}
                onChange={e => setCode(e.currentTarget.value)}
              />
              <Form.Control.Feedback type='invalid'>
                Verification code is required
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {/* Did we receive a verification error? */}
          {verificationError && (
            <div className='error'>
              <i className='icon bi-exclamation-circle'></i>{' '}
              <strong>Error: </strong> {' ' + verificationError}
            </div>
          )}

          <Button
            variant='primary'
            size='lg'
            type='submit'
            disabled={!!loading || !code}
            className='w-100 mt-3'
          >
            {!!loading ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                />
                <span className='px-2'>Loading...</span>
              </>
            ) : (
              <span>Verify</span>
            )}
          </Button>

          <p className='muted mt-5 mb-0 text-center'>
            Didn't get the email?
            <br />
            <Button
              variant='secondary-outline'
              disabled={!!loading || codeSent}
              onClick={requestNewCode}
            >
              <span>{codeSent ? 'Sent! 🙂' : 'Request a new code'}</span>
            </Button>
          </p>
        </Form>
      </Row>
    </Container>
  )
}
